import { useLocalStorageBackedState } from '../../../general/components/FullscreenPreview/viewers/common/3d/useLocalStoreBackedState';
import { GetStartedState, GetStartedStateSchema, LOCAL_STORAGE_KEY } from './model';
import { ALL_ONBOARDING_STEPS } from './steps';

/**
 * Дефолтное состояние виджета (свежий localStorage): первый шаг раскрыт, ничего не пройдено,
 * виджет виден. Экспортируется, чтобы императивный showGetStartedWidget не дублировал дефолт.
 */
export const DEFAULT_STATE: GetStartedState = {
  // Первый шаг — 'sandbox-folder'; гейт Magic Studio на него не влияет, поэтому
  // берём нефильтрованный список (стор здесь недоступен).
  openStepId: ALL_ONBOARDING_STEPS[0]?.id ?? null,
  completedStepIds: [],
  hidden: false,
};

export function useGetStartedState() {
  const [state, setState] = useLocalStorageBackedState(
    DEFAULT_STATE,
    GetStartedStateSchema,
    LOCAL_STORAGE_KEY,
  );

  // Дефолт (свежий localStorage) задаётся через DEFAULT_STATE: первый шаг раскрыт.
  // После взаимодействия сохраняется фактическое состояние пользователя — в т.ч. все секции
  // могут быть свёрнуты (openStepId === null). Принудительного инварианта больше нет.

  /** Открыть конкретный шаг или свернуть всё (stepId === null) — для Previous/Next и Collapse */
  const openStep = (stepId: string | null) => {
    setState((prev) => ({ ...prev, openStepId: stepId }));
  };

  /** Отметить шаг выполненным */
  const completeStep = (stepId: string) => {
    setState((prev) => ({
      ...prev,
      completedStepIds: prev.completedStepIds.includes(stepId)
        ? prev.completedStepIds
        : [...prev.completedStepIds, stepId],
    }));
  };

  /**
   * Идемпотентно добавляет stepId в completedStepIds для апдейтера состояния.
   * Возвращает прежний массив (без новой ссылки), если шаг уже отмечен.
   */
  const withCompleted = (prev: GetStartedState, stepId: string): string[] =>
    prev.completedStepIds.includes(stepId)
      ? prev.completedStepIds
      : [...prev.completedStepIds, stepId];

  /**
   * За ОДИН setState: отметить currentId выполненным (идемпотентно) И открыть nextId.
   * Объединённое обновление нужно, потому что хук применяет апдейтер к stale-замыканию
   * innerValue — два синхронных setState в одном тике затёрли бы друг друга.
   */
  const completeAndOpen = (currentId: string, nextId: string) => {
    setState((prev) => ({
      ...prev,
      completedStepIds: withCompleted(prev, currentId),
      openStepId: nextId,
    }));
  };

  /**
   * За ОДИН setState: отметить currentId выполненным (идемпотентно) И свернуть виджет.
   * Используется на последнем шаге по Next/«Got it».
   */
  const completeAndHide = (currentId: string) => {
    setState((prev) => ({
      ...prev,
      completedStepIds: withCompleted(prev, currentId),
      hidden: true,
    }));
  };

  /** Снять отметку выполнения (чекбокс двунаправленный) */
  const uncompleteStep = (stepId: string) => {
    setState((prev) => ({
      ...prev,
      completedStepIds: prev.completedStepIds.filter((id) => id !== stepId),
    }));
  };

  /** Скрыть виджет (плавное исчезновение + полный unmount управляется в GetStartedWidget) */
  const hideWidget = () => {
    setState((prev) => ({ ...prev, hidden: true }));
  };

  return {
    state,
    openStep,
    completeStep,
    completeAndOpen,
    completeAndHide,
    uncompleteStep,
    hideWidget,
  };
}
